import React from 'react';
import { Breadcrumb, Button, Card, Col, Divider, Row, Space } from 'antd';
import { Maybe } from 'graphql/jsutils/Maybe';
import { Link, useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { InfoCircleOutlined } from '@ant-design/icons';
import { postMessageAction, postMessageService } from '../shared/postMessageService';
import { PageRoutes } from '../../conf/Global';
import { GlossaryNode, GlossaryTerm, ParentNodesResult } from '../../types.generated';
import { BreadcrumbItem } from '../entity/shared/containers/profile/nav/ProfileNavBrowsePath';
import { useGlossaryEntityData } from '../entity/shared/GlossaryEntityContext';
import { useEntityRegistry } from '../useEntityRegistry';
import { useTranslation } from '../../i18n/useTranslation';
import { ReactComponent as ChangeLogIcon } from '../../images/icon-change-log.svg';
import { ReactComponent as RelationshipsIcon } from '../../images/icon-relationships.svg';
import { navigateToLineageUrl } from '../lineage/utils/navigateToLineageUrl';
import useIsChangeLogMode from '../entity/shared/containers/profile/utils/useIsChangeLogMode';

const BreadcrumbsWrapper = styled(Breadcrumb)`
    font-size: 16px;
`;

function getGlossaryBreadcrumbs(parentNodes: Maybe<ParentNodesResult>, entity: Maybe<GlossaryNode | GlossaryTerm>) {
    const nodes = parentNodes?.nodes;
    const breadcrumbs: (GlossaryTerm | GlossaryNode)[] = [...(nodes || [])].reverse();
    if (entity) breadcrumbs.push(entity);
    return breadcrumbs;
}
const GLOSSARY_TERM = 'GLOSSARY_TERM';
const GLOSSARY_NODE = 'GLOSSARY_NODE';

function GlossaryEntitiesPath() {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const { entityData } = useGlossaryEntityData();
    const history = useHistory();
    const location = useLocation();
    const isChangeLogMode = useIsChangeLogMode();

    const isGlossary = entityData?.type === GLOSSARY_TERM || entityData?.type === GLOSSARY_NODE;
    const breadcrumbs = getGlossaryBreadcrumbs(entityData?.parentNodes, entityData as GlossaryNode | GlossaryTerm);

    // Open the instance explorer modal
    const onRelationshipsClick = () => {
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_INSTANCE_EXPLORER_MODAL,
            urn: entityData?.urn,
            tableName: entityData?.properties?.name || entityData?.name,
        });
    };

    return (
        <>
            <Card className="px-3 py-2">
                <Row className="f-flex-column-xs" wrap={false} align="middle">
                    <Col flex="auto">
                        <BreadcrumbsWrapper separator=">">
                            <BreadcrumbItem>
                                <Link to={PageRoutes.GLOSSARY}>{t('Glossary')}</Link>
                            </BreadcrumbItem>
                            {breadcrumbs &&
                                breadcrumbs.map((node) => {
                                    return (
                                        <BreadcrumbItem key={node.urn}>
                                            <Link to={`${entityRegistry.getEntityUrl(node.type, node.urn)}`}>
                                                {entityRegistry.getDisplayName(node.type, node)}
                                            </Link>
                                        </BreadcrumbItem>
                                    );
                                })}
                        </BreadcrumbsWrapper>
                    </Col>
                    {isGlossary && (
                        <Col flex="none">
                            <Space size="middle">
                                <Button
                                    type="text"
                                    icon={<ChangeLogIcon />}
                                    className={`${isChangeLogMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                                    onClick={() => {
                                        navigateToLineageUrl({
                                            location,
                                            history,
                                            isLineageMode: false,
                                            isChangeLogMode: true,
                                        });
                                    }}
                                >
                                    {t('Change Log')}
                                </Button>
                                {entityData?.type === GLOSSARY_TERM && (
                                    <Button
                                        type="text"
                                        className="f-btn-flat"
                                        icon={<RelationshipsIcon />}
                                        onClick={onRelationshipsClick}
                                    >
                                        {t('Relationships')}
                                    </Button>
                                )}
                                <Button
                                    type="text"
                                    aria-label={t('Details')}
                                    icon={<InfoCircleOutlined aria-hidden="true" />}
                                    className={`${!isChangeLogMode ? 'f-btn-active' : ''} f-lineage-btn f-btn-flat`}
                                    onClick={() => {
                                        navigateToLineageUrl({
                                            location,
                                            history,
                                            isLineageMode: false,
                                            isChangeLogMode: false,
                                        });
                                    }}
                                >
                                    {t('Details')}
                                </Button>
                            </Space>
                        </Col>
                    )}
                </Row>
            </Card>
            <Divider className="ma-0" />
        </>
    );
}

export default GlossaryEntitiesPath;
